import type {
  GraphMember,
  TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';

export interface GatewayGraphMember {
  id: string;
  ownerUserId: string;
  name: string;
  relationship: string;
  side: string;
  approvalStatus: string;
  confirmationsCount: number;
  createdAt: string;
  parentIds?: string[] | null;
  spouseId?: string | null;
  claimedByUserId?: string | null;
  profileState?: string | null;
  relationshipOverride?: string | null;
  branchOwnerMemberId?: string | null;
  relationshipWorkflow?:
    TreeRelationshipWorkflow | null;
}

import {
  authorizeMandatoryTreeMutation,
  type MandatoryTreeMember,
  type MandatoryTreeMutationDecision,
  type TreeMutationMode,
} from './MandatoryTreeMutationAuthority';

import {
  buildProposedTreeMutation,
  type TreeMutationProposal,
} from './TreeMutationProposalBuilder';

import type {
  CanonicalTreeTopology,
} from './CanonicalTreeTopologyAuthority';

import {
  createCanonicalTreeMutationGatewayEvidenceContract,
  rebuildCanonicalTreeMutationAfterGraphEvidence,
} from './CanonicalTreeMutationGatewayEvidenceContract';

import {
  runCanonicalTreeTopologyReplacementSafetyGate,
} from './CanonicalTreeTopologyReplacementSafetyGate';

export interface GatewayTreeAuthorizationInput {
  members: GatewayGraphMember[];
  viewerMemberId: string | null;
  proposal: TreeMutationProposal;
  mode?: TreeMutationMode;
}

export interface GatewayTreeAuthorization {
  decision: MandatoryTreeMutationDecision;
  before: MandatoryTreeMember[];
  after: MandatoryTreeMember[];
  affectedMemberIds: string[];
  proposedViewerMemberId: string | null | undefined;
  beforeTopology: CanonicalTreeTopology;
  afterTopology: CanonicalTreeTopology;
}

export function mapGraphMemberForMandatoryAuthority(
  member: GatewayGraphMember,
): MandatoryTreeMember {
  return {
    id: member.id,
    parentIds: [...(member.parentIds ?? [])],
    spouseId: member.spouseId ?? null,
    claimedByUserId: member.claimedByUserId ?? null,
    profileState: member.profileState ?? null,
    approvalStatus: member.approvalStatus ?? null,
  };
}

export function authorizeGatewayTreeMutation(
  input: GatewayTreeAuthorizationInput,
): GatewayTreeAuthorization {
  const before = input.members.map(
    mapGraphMemberForMandatoryAuthority,
  );

  const proposal = buildProposedTreeMutation(
    before,
    input.proposal,
    input.viewerMemberId,
  );

  const proposedViewerMemberId =
    proposal.proposedViewerMemberId === undefined
      ? input.viewerMemberId
      : proposal.proposedViewerMemberId;

  const evidence =
    createCanonicalTreeMutationGatewayEvidenceContract({
      members:
        input.members as GraphMember[],
    });

  const afterGatewayMembers =
    rebuildCanonicalTreeMutationAfterGraphEvidence({
      beforeMembers:
        evidence.members,

      afterMembers:
        proposal.after,
    });

  const beforeTopologySafety =
    runCanonicalTreeTopologyReplacementSafetyGate({
      members:
        evidence.members,

      viewerMemberId:
        input.viewerMemberId,
    });

  const afterTopologySafety =
    runCanonicalTreeTopologyReplacementSafetyGate({
      members:
        afterGatewayMembers,

      viewerMemberId:
        proposedViewerMemberId,
    });

  const beforeTopology =
    beforeTopologySafety.selectedTopology;

  const afterTopology =
    afterTopologySafety.selectedTopology;

  const decision = authorizeMandatoryTreeMutation({
    before,
    after: proposal.after,
    viewerMemberId: input.viewerMemberId,
    proposedViewerMemberId:
      proposal.proposedViewerMemberId,
    affectedMemberIds:
      proposal.affectedMemberIds,
    mutationKind: input.proposal.kind,
    beforeTopology,
    afterTopology,
    mode: input.mode ?? 'normal',
  });

  return {
    decision,
    before,
    after: proposal.after,
    affectedMemberIds:
      proposal.affectedMemberIds,
    proposedViewerMemberId:
      proposal.proposedViewerMemberId,
    beforeTopology,
    afterTopology,
  };
}
